import { useQuery } from '@tanstack/react-query'
import epsClient from 'enrollment-platform/api/eps.client'

interface CustomerConfigurationResponse {
  value?: unknown
}

/**
 * Reads one customer configuration flag from the EPS platform-configurations store.
 *
 * Same contract as `useGlobalFlag`: `isEnabled` is true only for an explicit
 * true, and any error, including a missing row, resolves to false. `isLoading`
 * stays true until that answer arrives, including when the customer id or key
 * is empty so the query never runs.
 */
export default function useCustomerConfigurationFlag(
  customerId: string | undefined,
  key: string
): {
  isEnabled: boolean
  isLoading: boolean
} {
  const { data, isLoading } = useQuery({
    queryKey: ['customerConfigurationFlag', customerId, key],
    queryFn: async (): Promise<boolean> => {
      try {
        const response = await epsClient.get<CustomerConfigurationResponse>(
          `/customers/${customerId}/configurations/${key}`
        )
        return response.data?.value === true
      } catch {
        return false
      }
    },
    enabled: Boolean(customerId) && key.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  return {
    isEnabled: data === true,
    isLoading: isLoading || data === undefined,
  }
}
